import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useQuery } from "react-query";
import type { Notification } from "pages/api/[orgId]/notifications/types";
import {
  dismissNotification,
  getNotifications,
} from "pages/api-clients/integrations";
import { useQueryToasterErrors } from "./useToaster";

export type NotificationContext = {
  ready: boolean;
  notifications: Notification[];
  dismiss(id: Notification["id"]): Promise<void>;
  setPagination: React.Dispatch<
    React.SetStateAction<{
      page: number;
      pageSize: number;
    }>
  >;
};

const PAGESIZE = 10;

export function useNotifications({
  orgId,
}: {
  orgId: string;
}): NotificationContext {
  const [{ page, pageSize }, setPagination] = useState({
    page: 1,
    pageSize: PAGESIZE,
  });
  useEffect(() => setPagination({ page, pageSize }), [page, pageSize]);
  const query = useQuery(
    ["entityWork", { orgId, page, pageSize }],
    async () => getNotifications(orgId),
    {
      keepPreviousData: true,
      staleTime: 20_000,
    }
  );
  useQueryToasterErrors(query);
  const dismiss = useCallback(
    (id: Notification["id"]) =>
      dismissNotification(orgId!, id)
        .then(() => {
          query.refetch();
        })
        .catch((error) => console.error("error dismissing notif #", id, error)),
    [orgId, query]
  );
  const notifications = query.data?.data || [];
  const ready = !query.isLoading;
  return {
    notifications,
    ready,
    dismiss,
    setPagination,
  };
}

export const notificationContext = createContext<NotificationContext>(
  undefined as unknown as NotificationContext
);

export function useNotificationContext() {
  return useContext(notificationContext);
}
